import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, Search, Edit, Trash2, Play, Pause, RefreshCw,
  CheckCircle, Clock, AlertTriangle, Activity, Eye,
  Settings, Zap, ExternalLink, Copy,
  Terminal, Layers, XCircle, Globe
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { api } from '../../lib/api';
import { API_ENDPOINTS } from '../../config/api';
import { InstanceConfigurator } from './InstanceConfigurator';

interface ProviderTemplate {
  id: string;
  name: string;
  type: string;
  version: string;
  status: string;
}

interface ProviderInstance {
  id: string;
  name: string;
  templateId: string;
  templateName?: string;
  templateType?: string;
  status: string;
  webhookUrl?: string;
  lastTestedAt?: string;
  lastError?: string;
  healthStatus?: 'healthy' | 'degraded' | 'unhealthy';
  metrics?: {
    totalRequests: number;
    successRate: number;
    avgLatency: number;
  };
  createdAt: string;
  updatedAt?: string;
}

export function ProviderInstances() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isConfiguratorOpen, setIsConfiguratorOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<ProviderInstance | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Fetch instances
  const { data: instances, isLoading, refetch } = useQuery({
    queryKey: ['provider-instances', search, statusFilter, templateFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (templateFilter) params.append('templateId', templateFilter);
      const response = await api.get(`${API_ENDPOINTS.providerEngine.instances}?${params}`);
      // Backend returns { data: [], total: number }
      const result = response.data;
      return result.data || result;
    },
  });

  // Fetch templates for filter
  const { data: templates } = useQuery({
    queryKey: ['provider-templates-list'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.providerEngine.templates);
      // Backend returns { data: [], total: number }
      const result = response.data;
      return result.data || result;
    },
  });

  // Mutations
  const activateMutation = useMutation({
    mutationFn: (id: string) => api.post(`${API_ENDPOINTS.providerEngine.instances}/${id}/activate`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-instances'] }),
  });

  const suspendMutation = useMutation({
    mutationFn: (id: string) => api.post(`${API_ENDPOINTS.providerEngine.instances}/${id}/suspend`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-instances'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`${API_ENDPOINTS.providerEngine.instances}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-instances'] });
      setIsDeleteModalOpen(false);
      setSelectedInstance(null);
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => api.post(`${API_ENDPOINTS.providerEngine.instances}/${id}/test`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-instances'] }),
  });

  // Handlers
  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setIsCreateModalOpen(false);
    setIsConfiguratorOpen(true);
  };

  const handleEdit = (instance: ProviderInstance) => {
    setSelectedInstance(instance);
    setSelectedTemplateId(instance.templateId);
    setIsConfiguratorOpen(true);
  };

  const handleView = (instance: ProviderInstance) => {
    setSelectedInstance(instance);
    setIsViewModalOpen(true);
  };

  const handleDeleteConfirm = (instance: ProviderInstance) => {
    setSelectedInstance(instance);
    setIsDeleteModalOpen(true);
  };

  const handleTestConsole = (instance: ProviderInstance) => {
    navigate(`/admin/test-console?instanceId=${instance.id}`);
  };

  const handleConfiguratorClose = () => {
    setIsConfiguratorOpen(false);
    setSelectedInstance(null);
    setSelectedTemplateId('');
    queryClient.invalidateQueries({ queryKey: ['provider-instances'] });
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Ativo</Badge>;
      case 'PENDING':
        return <Badge variant="warning"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'TESTING':
        return <Badge variant="info"><Activity className="w-3 h-3 mr-1" />Testando</Badge>;
      case 'SUSPENDED':
        return <Badge variant="error"><Pause className="w-3 h-3 mr-1" />Suspenso</Badge>;
      case 'ERROR':
        return <Badge variant="error"><AlertTriangle className="w-3 h-3 mr-1" />Erro</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Get health indicator
  const getHealthIndicator = (health?: string) => {
    switch (health) {
      case 'healthy':
        return <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Saudável" />;
      case 'degraded':
        return <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" title="Degradado" />;
      case 'unhealthy':
        return <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title="Não saudável" />;
      default:
        return <div className="w-2 h-2 rounded-full bg-surface-500" title="Desconhecido" />;
    }
  };

  // Stats
  const stats = {
    total: instances?.length || 0,
    active: instances?.filter((i: ProviderInstance) => i.status === 'ACTIVE').length || 0,
    pending: instances?.filter((i: ProviderInstance) => i.status === 'PENDING').length || 0,
    error: instances?.filter((i: ProviderInstance) => i.status === 'ERROR').length || 0,
  };

  // Active templates for selection
  const activeTemplates = templates?.filter((t: ProviderTemplate) => t.status === 'ACTIVE') || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-white">Provider Instances</h1>
          <p className="text-surface-400 mt-1">
            Gerencie instâncias de providers configurados com credenciais
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/admin/test-console')}>
            <Terminal className="w-4 h-4 mr-2" />
            Test Console
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Instância
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Layers className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-xs text-surface-400">Total de Instâncias</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.active}</p>
              <p className="text-xs text-surface-400">Ativas</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.pending}</p>
              <p className="text-xs text-surface-400">Pendentes</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.error}</p>
              <p className="text-xs text-surface-400">Com Erro</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Buscar instâncias por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <Select
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            options={[
              { value: '', label: 'Todos os templates' },
              ...activeTemplates.map((t: ProviderTemplate) => ({
                value: t.id,
                label: `${t.name} (${t.type})`,
              })),
            ]}
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: '', label: 'Todos os status' },
              { value: 'ACTIVE', label: 'Ativo' },
              { value: 'PENDING', label: 'Pendente' },
              { value: 'TESTING', label: 'Testando' },
              { value: 'SUSPENDED', label: 'Suspenso' },
              { value: 'ERROR', label: 'Erro' },
            ]}
          />
          <Button variant="ghost" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Instances List */}
        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-3" />
            <p className="text-surface-400">Carregando instâncias...</p>
          </div>
        ) : !instances?.length ? (
          <div className="text-center py-12 border-2 border-dashed border-surface-600 rounded-lg">
            <Settings className="w-12 h-12 text-surface-500 mx-auto mb-3" />
            <p className="text-surface-400 mb-2">Nenhuma instância encontrada</p>
            <p className="text-sm text-surface-500 mb-4">
              Crie uma instância para começar a usar um provider
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Instância
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {instances.map((instance: ProviderInstance) => (
              <div
                key={instance.id}
                className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg border border-surface-700 hover:border-primary-500/50 transition-all group"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {getHealthIndicator(instance.healthStatus)}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      instance.status === 'ACTIVE' ? 'bg-green-500/20' :
                      instance.status === 'ERROR' ? 'bg-red-500/20' :
                      'bg-surface-700'
                    }`}>
                      <Globe className={`w-5 h-5 ${
                        instance.status === 'ACTIVE' ? 'text-green-400' :
                        instance.status === 'ERROR' ? 'text-red-400' :
                        'text-surface-400'
                      }`} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white truncate">{instance.name}</h3>
                    </div>
                    <p className="text-sm text-surface-400 truncate">
                      Template: {instance.templateName || instance.templateId}
                      {instance.templateType && <span className="text-surface-500"> ({instance.templateType})</span>}
                    </p>
                    {instance.lastError && (
                      <p className="text-xs text-red-400 mt-1 truncate">{instance.lastError}</p>
                    )}
                    {instance.metrics && (
                      <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
                        <span>{instance.metrics.totalRequests} requisições</span>
                        <span>{instance.metrics.successRate}% sucesso</span>
                        <span>{instance.metrics.avgLatency}ms latência</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {getStatusBadge(instance.status)}
                  
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => handleView(instance)} title="Visualizar">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleTestConsole(instance)} title="Test Console">
                      <Terminal className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => testMutation.mutate(instance.id)}
                      title="Testar Conexão"
                      disabled={testMutation.isPending}
                    >
                      <Zap className="w-4 h-4" />
                    </Button>
                    {instance.status === 'ACTIVE' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => suspendMutation.mutate(instance.id)}
                        title="Suspender"
                      >
                        <Pause className="w-4 h-4" />
                      </Button>
                    ) : instance.status !== 'ERROR' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => activateMutation.mutate(instance.id)}
                        title="Ativar"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(instance)} title="Editar">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteConfirm(instance)} title="Excluir">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create Instance Modal - Template Selection */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nova Provider Instance"
      >
        <div className="space-y-6">
          <p className="text-surface-400">
            Selecione um template para criar uma nova instância de provider.
          </p>

          {activeTemplates.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-surface-600 rounded-lg">
              <Layers className="w-10 h-10 text-surface-500 mx-auto mb-3" />
              <p className="text-surface-400 mb-2">Nenhum template disponível</p>
              <p className="text-sm text-surface-500 mb-4">
                Crie um template primeiro para poder criar instâncias
              </p>
              <Button onClick={() => navigate('/admin/provider-templates')}>
                Ir para Templates
              </Button>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activeTemplates.map((template: ProviderTemplate) => (
                <button
                  key={template.id}
                  onClick={() => handleSelectTemplate(template.id)}
                  className="w-full flex items-center gap-4 p-4 bg-surface-800 border border-surface-600 rounded-lg hover:border-primary-500 hover:bg-surface-700 transition-all text-left"
                >
                  <div className="p-2 bg-primary-500/20 rounded-lg">
                    <Globe className="w-5 h-5 text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white">{template.name}</p>
                      <span className="text-xs text-surface-500 bg-surface-700 px-2 py-0.5 rounded">
                        v{template.version}
                      </span>
                    </div>
                    <p className="text-sm text-surface-400">{template.type}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-surface-500" />
                </button>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-surface-700">
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Instance Configurator Modal */}
      {isConfiguratorOpen && (
        <InstanceConfigurator
          isOpen={isConfiguratorOpen}
          onClose={handleConfiguratorClose}
          templateId={selectedTemplateId}
          instanceId={selectedInstance?.id}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar Exclusão"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-400 font-medium">Atenção!</p>
              <p className="text-sm text-surface-300 mt-1">
                Você está prestes a excluir a instância <strong className="text-white">{selectedInstance?.name}</strong>.
                Esta ação não pode ser desfeita e todas as credenciais serão removidas.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => selectedInstance && deleteMutation.mutate(selectedInstance.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Excluindo...' : 'Excluir Instância'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Instance Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={selectedInstance?.name || 'Detalhes da Instância'}
      >
        {selectedInstance && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${
                selectedInstance.status === 'ACTIVE' ? 'bg-green-500/20' :
                selectedInstance.status === 'ERROR' ? 'bg-red-500/20' :
                'bg-surface-700'
              }`}>
                <Globe className={`w-6 h-6 ${
                  selectedInstance.status === 'ACTIVE' ? 'text-green-400' :
                  selectedInstance.status === 'ERROR' ? 'text-red-400' :
                  'text-surface-400'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedInstance.name}</h3>
                <p className="text-sm text-surface-400">
                  Template: {selectedInstance.templateName || selectedInstance.templateId}
                </p>
              </div>
            </div>

            {/* Status */}
            <div className="flex gap-2">
              {getStatusBadge(selectedInstance.status)}
              {selectedInstance.healthStatus && (
                <Badge variant={
                  selectedInstance.healthStatus === 'healthy' ? 'success' :
                  selectedInstance.healthStatus === 'degraded' ? 'warning' : 'error'
                }>
                  {selectedInstance.healthStatus === 'healthy' ? 'Saudável' :
                   selectedInstance.healthStatus === 'degraded' ? 'Degradado' : 'Não saudável'}
                </Badge>
              )}
            </div>

            {/* Error */}
            {selectedInstance.lastError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs text-red-400 font-medium mb-1">Último Erro</p>
                <p className="text-sm text-red-300">{selectedInstance.lastError}</p>
              </div>
            )}

            {/* Metrics */}
            {selectedInstance.metrics && (
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-surface-800 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{selectedInstance.metrics.totalRequests}</p>
                  <p className="text-xs text-surface-400">Requisições</p>
                </div>
                <div className="p-3 bg-surface-800 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{selectedInstance.metrics.successRate}%</p>
                  <p className="text-xs text-surface-400">Taxa de Sucesso</p>
                </div>
                <div className="p-3 bg-surface-800 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{selectedInstance.metrics.avgLatency}ms</p>
                  <p className="text-xs text-surface-400">Latência Média</p>
                </div>
              </div>
            )}

            {/* Webhook URL */}
            {selectedInstance.webhookUrl && (
              <div className="p-3 bg-surface-800 rounded-lg">
                <p className="text-xs text-surface-400 mb-1">Webhook URL</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-white font-mono flex-1 truncate">
                    {selectedInstance.webhookUrl}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigator.clipboard.writeText(selectedInstance.webhookUrl!)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-surface-400">Criado em</p>
                <p className="text-white">{new Date(selectedInstance.createdAt).toLocaleString('pt-BR')}</p>
              </div>
              {selectedInstance.lastTestedAt && (
                <div>
                  <p className="text-surface-400">Último teste</p>
                  <p className="text-white">{new Date(selectedInstance.lastTestedAt).toLocaleString('pt-BR')}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-surface-700">
              <Button variant="secondary" onClick={() => setIsViewModalOpen(false)}>
                Fechar
              </Button>
              <Button variant="secondary" onClick={() => handleTestConsole(selectedInstance)}>
                <Terminal className="w-4 h-4 mr-2" />
                Test Console
              </Button>
              <Button onClick={() => {
                setIsViewModalOpen(false);
                handleEdit(selectedInstance);
              }}>
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ProviderInstances;
